import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { getFreshToken } from '@/lib/auth-token';

interface SettingRow { key: string; value: string; label: string; }

const AVALIACOES_CONFIG_2023_2024 = [
  { key: 'pontos_excelente_2023', label: 'Excelente', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', icon: 'ri-star-fill text-emerald-500', barColor: 'bg-emerald-400' },
  { key: 'pontos_muito_bom_2023', label: 'Muito Bom', color: 'bg-teal-100 text-teal-700', border: 'border-teal-200', icon: 'ri-star-half-fill text-teal-500', barColor: 'bg-teal-400' },
  { key: 'pontos_bom_2023', label: 'Bom', color: 'bg-amber-100 text-amber-700', border: 'border-amber-200', icon: 'ri-thumb-up-line text-amber-500', barColor: 'bg-amber-400' },
  { key: 'pontos_regular_2023', label: 'Regular', color: 'bg-orange-100 text-orange-700', border: 'border-orange-200', icon: 'ri-subtract-line text-orange-500', barColor: 'bg-orange-400' },
  { key: 'pontos_inadequado_2023', label: 'Inadequado', color: 'bg-slate-100 text-slate-500', border: 'border-slate-200', icon: 'ri-close-line text-slate-400', barColor: 'bg-slate-300' },
];

const AVALIACOES_CONFIG_2025 = [
  { key: 'pontos_excelente', label: 'Excelente', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', icon: 'ri-star-fill text-emerald-500', barColor: 'bg-emerald-400' },
  { key: 'pontos_muito_bom', label: 'Muito Bom', color: 'bg-teal-100 text-teal-700', border: 'border-teal-200', icon: 'ri-star-half-fill text-teal-500', barColor: 'bg-teal-400' },
  { key: 'pontos_bom', label: 'Bom', color: 'bg-amber-100 text-amber-700', border: 'border-amber-200', icon: 'ri-thumb-up-line text-amber-500', barColor: 'bg-amber-400' },
  { key: 'pontos_regular', label: 'Regular', color: 'bg-orange-100 text-orange-700', border: 'border-orange-200', icon: 'ri-subtract-line text-orange-500', barColor: 'bg-orange-400' },
  { key: 'pontos_inadequado', label: 'Inadequado', color: 'bg-slate-100 text-slate-500', border: 'border-slate-200', icon: 'ri-close-line text-slate-400', barColor: 'bg-slate-300' },
];

const GENERAL_KEYS = ['nome_camara', 'ciclo_siadap', 'ano_progressao', 'email_notificacoes'];

const SAVE_SETTINGS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/save-settings`;

// Defaults para os novos campos 2023-2024 (caso não existam ainda na BD)
const DEFAULTS_2023: Record<string, string> = {
  pontos_progressao_2023: '8',
  pontos_excelente_2023: '6',
  pontos_muito_bom_2023: '4',
  pontos_bom_2023: '3',
  pontos_regular_2023: '2',
  pontos_inadequado_2023: '0',
};

export default function SettingsTab() {
  const { refetch } = useAppSettings();
  const { reload: reloadFuncionarios } = useFuncionarios();
  const { activeMunicipio } = useActiveMunicipio();
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState<'pontos' | 'historico' | null>(null);
  const [resetting, setResetting] = useState(false);

  const municipioId = activeMunicipio?.id ?? '00000000-0000-0000-0000-000000000001';

  useEffect(() => {
    supabase
      .from('app_settings')
      .select('*')
      .eq('municipio_id', municipioId)
      .order('key')
      .then(({ data }) => {
        if (data) {
          setSettings(data);
          const e: Record<string, string> = { ...DEFAULTS_2023 };
          data.forEach((s: SettingRow) => { e[s.key] = s.value; });
          setEdits(e);
        }
      });
  }, [municipioId]);

  const set = (key: string, val: string) => setEdits((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const token = await getFreshToken();

      const res = await fetch(SAVE_SETTINGS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ settings: edits }),
      });

      const json = await res.json();
      if (!res.ok) {
        setMsg({ type: 'error', text: json.error ?? `Erro ${res.status}` });
        return;
      }

      await refetch();
      setMsg({ type: 'success', text: 'Configurações guardadas com sucesso!' });
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro desconhecido.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  const generalFields = settings.filter((s) => GENERAL_KEYS.includes(s.key));

  async function handleReset(tipo: 'pontos' | 'historico') {
    setResetting(true);
    try {

      if (tipo === 'pontos') {
        const { error: errPontos } = await supabase
          .from('funcionarios')
          .update({ pontos_atuais: 0, pontos_descontados: 0 })
          .eq('municipio_id', municipioId);

        if (errPontos) throw new Error(errPontos.message);

        const { error: errProgs } = await supabase
          .from('progressoes')
          .delete()
          .eq('municipio_id', municipioId);

        if (errProgs) throw new Error(errProgs.message);

        const { error: errHistProg } = await supabase
          .from('historico_progressoes')
          .delete()
          .eq('municipio_id', municipioId);

        if (errHistProg) throw new Error(errHistProg.message);

        await reloadFuncionarios();
        setMsg({ type: 'success', text: 'Pontos de todos os funcionários colocados a zero!' });
      } else {
        const { error: errHist } = await supabase
          .from('historico_alteracoes')
          .delete()
          .eq('municipio_id', municipioId);

        if (errHist) throw new Error(errHist.message);
        setMsg({ type: 'success', text: 'Histórico de alterações limpo com sucesso!' });
      }
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao executar operação.' });
    } finally {
      setResetting(false);
      setConfirmReset(null);
      setTimeout(() => setMsg(null), 6000);
    }
  }

  function renderAvaliacaoRow(
    av: { key: string; label: string; color: string; border: string; icon: string; barColor: string },
    maxPts = 10
  ) {
    const val = edits[av.key] ?? '';
    const numVal = Number(val);
    const pct = Math.min((numVal / maxPts) * 100, 100);
    return (
      <div key={av.key} className={`flex items-center gap-4 p-3 rounded-lg border ${av.border} bg-white`}>
        <div className="w-24 flex-shrink-0">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${av.color}`}>
            <i className={`${av.icon} text-xs`} />
            {av.label}
          </span>
        </div>
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${av.barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="number"
            min={0} max={20} step={0.5}
            value={val}
            onChange={(e) => set(av.key, e.target.value)}
            className="w-16 px-2 py-1.5 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20"
          />
          <span className="text-xs text-slate-400 w-5">pts</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Pontos para Progressão ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
            <i className="ri-medal-line text-amber-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Pontos para Progressão</h2>
            <p className="text-xs text-slate-400 mt-0.5">Número de pontos necessários para progredir de nível</p>
          </div>
        </div>

        <div className="p-6 space-y-4">

          {/* Período 2000–2022 */}
          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                <i className="ri-calendar-line text-xs" />
                Avaliações 2000 – 2022
              </span>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Pontos necessários (1 nível)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1} max={100} step={0.5}
                  value={edits['pontos_progressao_pre2025'] ?? '10'}
                  onChange={(e) => set('pontos_progressao_pre2025', e.target.value)}
                  className="w-20 px-3 py-1.5 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                />
                <span className="text-sm text-slate-400">pts</span>
              </div>
            </div>
            <input
              type="range" min={1} max={100} step={0.5}
              value={edits['pontos_progressao_pre2025'] ?? '10'}
              onChange={(e) => set('pontos_progressao_pre2025', e.target.value)}
              className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-slate-700"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>1 pt</span><span>100 pts</span>
            </div>
          </div>

          {/* Período 2023–2024 */}
          <div className="rounded-xl border border-sky-200 p-4 space-y-3 bg-sky-50/30">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">
                <i className="ri-calendar-2-line text-xs" />
                Avaliações 2023 – 2024
              </span>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Pontos necessários (1 nível)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1} max={100} step={0.5}
                  value={edits['pontos_progressao_2023'] ?? '8'}
                  onChange={(e) => set('pontos_progressao_2023', e.target.value)}
                  className="w-20 px-3 py-1.5 text-sm font-bold text-center border border-sky-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400/30"
                />
                <span className="text-sm text-slate-400">pts</span>
              </div>
            </div>
            <input
              type="range" min={1} max={100} step={0.5}
              value={edits['pontos_progressao_2023'] ?? '8'}
              onChange={(e) => set('pontos_progressao_2023', e.target.value)}
              className="w-full h-2 bg-sky-100 rounded-full appearance-none cursor-pointer accent-sky-600"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>1 pt</span><span>100 pts</span>
            </div>
          </div>

          {/* Período 2025+ */}
          <div className="rounded-xl border border-amber-200 p-4 space-y-3 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                <i className="ri-calendar-check-line text-xs" />
                Avaliações 2025 em diante
              </span>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Pontos necessários (1 nível)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1} max={100} step={0.5}
                  value={edits['pontos_progressao'] ?? '8'}
                  onChange={(e) => set('pontos_progressao', e.target.value)}
                  className="w-20 px-3 py-1.5 text-sm font-bold text-center border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                />
                <span className="text-sm text-slate-400">pts</span>
              </div>
            </div>
            <input
              type="range" min={1} max={100} step={0.5}
              value={edits['pontos_progressao'] ?? '8'}
              onChange={(e) => set('pontos_progressao', e.target.value)}
              className="w-full h-2 bg-amber-100 rounded-full appearance-none cursor-pointer accent-amber-600"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>1 pt</span><span>100 pts</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── Pontos por Avaliação ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
            <i className="ri-bar-chart-2-line text-teal-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Pontos por Avaliação</h2>
            <p className="text-xs text-slate-400 mt-0.5">Escala de pontos aplicada conforme o período da avaliação</p>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* Período 2000–2022 — editável */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                <i className="ri-calendar-line text-xs" />
                Avaliações 2000 – 2022
              </span>
              <span className="text-xs text-slate-400 ml-1">Valores configuráveis</span>
            </div>
            <div className="p-4 space-y-2">
              {[
                { key: 'pontos_excelente_pre2023', label: 'Excelente', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', icon: 'ri-star-fill text-emerald-500', barColor: 'bg-emerald-400', defaultVal: '6' },
                { key: 'pontos_relevante_pre2023', label: 'Relevante', color: 'bg-teal-100 text-teal-700', border: 'border-teal-200', icon: 'ri-star-half-fill text-teal-500', barColor: 'bg-teal-400', defaultVal: '4' },
                { key: 'pontos_adequado_pre2023', label: 'Adequado', color: 'bg-amber-100 text-amber-700', border: 'border-amber-200', icon: 'ri-thumb-up-line text-amber-500', barColor: 'bg-amber-400', defaultVal: '2' },
                { key: 'pontos_inadequado_pre2023', label: 'Inadequado', color: 'bg-red-100 text-red-600', border: 'border-red-200', icon: 'ri-close-line text-red-400', barColor: 'bg-red-400', defaultVal: '-2' },
              ].map((av) => {
                const val = edits[av.key] ?? av.defaultVal;
                const numVal = Number(val);
                const isNegative = numVal < 0;
                const pct = isNegative ? Math.min((Math.abs(numVal) / 10) * 100, 100) : Math.min((numVal / 10) * 100, 100);
                return (
                  <div key={av.key} className={`flex items-center gap-4 p-3 rounded-lg border ${av.border} bg-white`}>
                    <div className="w-24 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${av.color}`}>
                        <i className={`${av.icon} text-xs`} />
                        {av.label}
                      </span>
                    </div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isNegative ? 'bg-red-400' : av.barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="number"
                        min={-10} max={20} step={0.5}
                        value={val}
                        onChange={(e) => set(av.key, e.target.value)}
                        className="w-16 px-2 py-1.5 text-sm font-bold text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                      />
                      <span className="text-xs text-slate-400 w-5">pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Período 2023–2024 — editável */}
          <div className="rounded-xl border border-sky-200 overflow-hidden">
            <div className="px-4 py-3 bg-sky-50/40 border-b border-sky-200 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">
                <i className="ri-calendar-2-line text-xs" />
                Avaliações 2023 – 2024
              </span>
              <span className="text-xs text-slate-400 ml-1">Valores configuráveis</span>
            </div>
            <div className="p-4 space-y-2">
              {AVALIACOES_CONFIG_2023_2024.map((av) => renderAvaliacaoRow(av))}
            </div>
          </div>

          {/* Período 2025+ — editável */}
          <div className="rounded-xl border border-amber-200 overflow-hidden">
            <div className="px-4 py-3 bg-amber-50/40 border-b border-amber-200 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                <i className="ri-calendar-check-line text-xs" />
                Avaliações 2025 em diante
              </span>
              <span className="text-xs text-slate-400 ml-1">Valores configuráveis</span>
            </div>
            <div className="p-4 space-y-2">
              {AVALIACOES_CONFIG_2025.map((av) => renderAvaliacaoRow(av))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Configurações Gerais ── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
            <i className="ri-settings-3-line text-slate-600 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Configurações Gerais</h2>
            <p className="text-xs text-slate-400 mt-0.5">Informações gerais da aplicação e do ciclo SIADAP</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* Nome e ciclo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <i className="ri-government-line text-xs mr-1.5 text-slate-400" />
                Nome da Câmara Municipal
              </label>
              <input
                type="text"
                placeholder="ex: Câmara Municipal de Lisboa"
                value={edits['nome_camara'] ?? ''}
                onChange={(e) => set('nome_camara', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <i className="ri-calendar-2-line text-xs mr-1.5 text-slate-400" />
                Ciclo SIADAP (ex: 2023-2024)
              </label>
              <input
                type="text"
                placeholder="ex: 2023-2024"
                value={edits['ciclo_siadap'] ?? ''}
                onChange={(e) => set('ciclo_siadap', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors"
              />
            </div>
          </div>

          {/* Datas do ciclo */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-calendar-check-line text-slate-400" />
              Datas do Ciclo de Avaliação
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Ano de início do ciclo</label>
                <input
                  type="number"
                  min={2000} max={2100}
                  placeholder="ex: 2023"
                  value={edits['ciclo_ano_inicio'] ?? '2023'}
                  onChange={(e) => set('ciclo_ano_inicio', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Ano de fim do ciclo</label>
                <input
                  type="number"
                  min={2000} max={2100}
                  placeholder="ex: 2025"
                  value={edits['ciclo_ano_fim'] ?? '2025'}
                  onChange={(e) => set('ciclo_ano_fim', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Ano de referência progressão</label>
                <input
                  type="number"
                  min={2000} max={2100}
                  placeholder="ex: 2024"
                  value={edits['ano_progressao'] ?? '2024'}
                  onChange={(e) => set('ano_progressao', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Regra especial 18 anos */}
          <div className="rounded-xl border border-amber-100 bg-amber-50/30 p-4 space-y-3">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-award-line text-amber-500" />
              Regra Especial — 18 Anos de Serviço
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Pontos mínimos para aplicar a regra</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1} max={20} step={0.5}
                    placeholder="ex: 6"
                    value={edits['pontos_regra_18_anos'] ?? '6'}
                    onChange={(e) => set('pontos_regra_18_anos', e.target.value)}
                    className="w-24 px-3 py-2 text-sm font-bold text-center border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/40 transition-colors"
                  />
                  <span className="text-sm text-slate-400">pts</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Limiar reduzido para funcionários com 18+ anos de serviço</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Data de referência (DD/MM/AAAA)</label>
                <input
                  type="text"
                  placeholder="ex: 30/08/2023"
                  value={edits['data_referencia_18_anos'] ?? '30/08/2023'}
                  onChange={(e) => set('data_referencia_18_anos', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/40 transition-colors"
                />
                <p className="text-xs text-slate-400 mt-1">Data a que o funcionário deve ter 18+ anos de serviço</p>
              </div>
            </div>
          </div>

          {/* Email e notificações */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-mail-settings-line text-slate-400" />
              Notificações por Email
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email para alertas de progressão</label>
                <input
                  type="email"
                  placeholder="ex: rh@camara.pt"
                  value={edits['email_notificacoes'] ?? ''}
                  onChange={(e) => set('email_notificacoes', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Email do remetente (nome)</label>
                <input
                  type="text"
                  placeholder="ex: Sistema SIADAP"
                  value={edits['email_remetente_nome'] ?? 'Sistema SIADAP'}
                  onChange={(e) => set('email_remetente_nome', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Exportação PDF */}
          <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <i className="ri-file-pdf-line text-slate-400" />
              Exportação PDF
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Título do documento PDF</label>
                <input
                  type="text"
                  placeholder="ex: Relatório de Progressões SIADAP"
                  value={edits['pdf_titulo'] ?? 'Relatório de Progressões SIADAP'}
                  onChange={(e) => set('pdf_titulo', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Rodapé do documento PDF</label>
                <input
                  type="text"
                  placeholder="ex: Câmara Municipal — Confidencial"
                  value={edits['pdf_rodape'] ?? ''}
                  onChange={(e) => set('pdf_rodape', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 transition-colors"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Manutenção ── */}
      <div className="bg-white rounded-2xl border border-red-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-red-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
            <i className="ri-tools-line text-red-500 text-base" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Manutenção</h2>
            <p className="text-xs text-slate-400 mt-0.5">Operações irreversíveis — use com cuidado</p>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-700">Colocar pontos a zero</p>
              <p className="text-xs text-slate-400 mt-0.5">Remove todos os pontos acumulados e descontados de todos os funcionários, e apaga as progressões registadas.</p>
            </div>
            <button
              onClick={() => setConfirmReset('pontos')}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-restart-line" />
              Resetar Pontos
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-700">Limpar histórico de alterações</p>
              <p className="text-xs text-slate-400 mt-0.5">Apaga todos os registos do histórico de alterações (criações, edições, eliminações).</p>
            </div>
            <button
              onClick={() => setConfirmReset('historico')}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-delete-bin-line" />
              Limpar Histórico
            </button>
          </div>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${msg.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-line text-green-500' : 'ri-error-warning-line text-red-500'}`} />
          </div>
          <p className={`text-sm ${msg.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>
        </div>
      )}

      {/* Modal confirmação reset */}
      {confirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !resetting && setConfirmReset(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-xl" />
              </div>
              <h3 className="font-bold text-slate-800 text-base text-center mb-2">Tens a certeza?</h3>
              <p className="text-sm text-slate-500 text-center">
                {confirmReset === 'pontos'
                  ? 'Esta ação irá colocar os pontos de TODOS os funcionários a zero e apagar todas as progressões registadas. Esta operação é irreversível.'
                  : 'Esta ação irá apagar todo o histórico de alterações. Esta operação é irreversível.'}
              </p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-center gap-3">
              <button
                onClick={() => setConfirmReset(null)}
                disabled={resetting}
                className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleReset(confirmReset)}
                disabled={resetting}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {resetting
                  ? <><i className="ri-loader-4-line animate-spin" />A processar...</>
                  : <><i className="ri-check-line" />Confirmar</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pb-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {saving
            ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
            : <><i className="ri-save-line" />Guardar Configurações</>
          }
        </button>
      </div>
    </div>
  );
}
